import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import { Message } from '@/types/message';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/Accordion';
import { Checkbox } from '@/ui/Checkbox';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import ButtonGroup from '@/ui/components/ButtonGroup';
import Spinner from '@/ui/components/Spinner';

import { MessageFilters } from './types';

interface Props {
  filters: MessageFilters;
  messages: Message[];
  onFiltersChange: (filters: MessageFilters) => void;
}

const CHANNEL_OPTIONS = [
  { id: undefined, label: 'All Channels' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
  { id: 'chat', label: 'Chat' },
  { id: 'voice', label: 'Call' }
];

interface AccordionFilters {
  created_by_id?: string;
  showAutoMessages: boolean;
}

const ApplyFiltersButton = ({ isSubmitting }: { isSubmitting: boolean }) => (
  <div className="mt-1">
    <Button color={Color.INDIGO} disabled={isSubmitting} size="xs" type="submit">
      {isSubmitting ? <Spinner /> : 'Apply'}
    </Button>
  </div>
);

const MessageFilterBar = ({ filters, messages, onFiltersChange }: Props) => {
  const senderOptions = useMemo(() => {
    const seen = new Set<string>();
    return messages
      .filter((m) => m.created_by)
      .reduce<{ label: string; value: string }[]>((acc, m) => {
        const staff = m.created_by!;
        if (!seen.has(staff.id)) {
          seen.add(staff.id);
          acc.push({
            label: [staff.first_name, staff.last_name].filter(Boolean).join(' ') || staff.id,
            value: staff.id
          });
        }
        return acc;
      }, []);
  }, [messages]);

  const {
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<AccordionFilters>({
    values: {
      created_by_id: filters.created_by_id,
      showAutoMessages: filters.showAutoMessages
    }
  });

  const applyFilters = (values: AccordionFilters) => {
    onFiltersChange({ ...filters, ...values });
  };

  return (
    <div data-testid="message-filter-bar">
      <form className="relative" onSubmit={handleSubmit(applyFilters)}>
        <FiltersBar
          search={
            <ButtonGroup
              buttons={CHANNEL_OPTIONS.map((opt) => ({
                size: 'xs' as const,
                color: filters.channel === opt.id ? Color.INDIGO : Color.WHITE,
                onClick: () => onFiltersChange({ ...filters, channel: opt.id }),
                children: opt.label
              }))}
            />
          }
          filterSets={
            <Accordion className="gap-1">
              {senderOptions.length > 0 && (
                <AccordionItem>
                  <AccordionTrigger>Sender</AccordionTrigger>
                  <AccordionContent>
                    <Controller
                      control={control}
                      name="created_by_id"
                      render={({ field }) => (
                        <AutocompleteSingleSelect
                          clearable
                          label="Sender"
                          showLabel={false}
                          onChange={(selection) => field.onChange(selection?.value)}
                          options={senderOptions}
                          placeholder="Filter by sender"
                          selectedOption={
                            senderOptions.find((o) => o.value === field.value) ?? emptySelectOption
                          }
                        />
                      )}
                    />
                    <ApplyFiltersButton isSubmitting={isSubmitting} />
                  </AccordionContent>
                </AccordionItem>
              )}
              <AccordionItem>
                <AccordionTrigger>Automated Messages</AccordionTrigger>
                <AccordionContent>
                  <Controller
                    control={control}
                    name="showAutoMessages"
                    render={({ field }) => (
                      <Checkbox
                        id="show-auto-messages"
                        checked={field.value}
                        label="Show automated messages"
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <ApplyFiltersButton isSubmitting={isSubmitting} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          }
        />
      </form>
    </div>
  );
};

export default MessageFilterBar;
